import {
  Badge,
  Button,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  Tooltip,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import type { DriverLogCell, DriverLogStatus, DriverLogWeek } from '@worklogix/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SHRINKABLE } from '../../responsive';
import { useTimeAdminApi } from '../api';

/**
 * Driver Logs — the weekly hours grid.
 *
 * The source is a spreadsheet with DRIVERS AS COLUMNS and dates as rows. This renders
 * it the other way up — one row per driver, one column per day — because a roster
 * grows downward far more comfortably than sideways, and a supervisor reads a person's
 * week along a row.
 *
 * THREE THINGS THE GRID SHOWS THAT THE SPREADSHEET CANNOT:
 *
 *  - PTO days, read-only, pulled from the actual booking. The sheet writes "pto" by
 *    hand; here it is the real record, so it cannot disagree with what PTO says.
 *  - The REGULAR/OVERTIME split, derived from the whole week. A driver on this sheet
 *    routinely works 50–60 hours, so what is regular and what is overtime is the
 *    interesting number, and it is not something a column of hours can tell you.
 *  - VISA drivers marked as overtime-exempt, because "why is none of this overtime?"
 *    is otherwise a mystery.
 *
 * Every write returns the WHOLE week from the API and replaces state wholesale: one
 * cell changes that driver's split for the entire week, so patching locally would show
 * totals that are quietly wrong.
 *
 * THIS GRID IS ALSO THE REVIEW SURFACE (migration 0057). Driver hours used to be
 * postable the instant they were typed — the poster asked only "has this gone yet?" —
 * so the next press of Post to NetSuite sent them unreviewed. Now every cell carries a
 * status and only APPROVED hours post, which makes three things the grid must show:
 * which days are still waiting on a person, which are cleared to go, and which have
 * already gone and are therefore frozen.
 */

const useStyles = makeStyles({
  bar: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalM,
    flexWrap: 'wrap',
  },
  spacer: { flexGrow: 1 },
  scroller: {
    overflowX: 'auto',
    // A data table stays TABULAR on a phone and scrolls sideways INSIDE this box —
    // columns like date/hours/status are only meaningful next to each other. The
    // SHRINKABLE part is what makes that work: without minWidth:0 this box refuses to
    // become narrower than the table and the page scrolls instead.
    ...SHRINKABLE,
    WebkitOverflowScrolling: 'touch',
  },
  grid: { borderCollapse: 'collapse', minWidth: '100%' },
  th: {
    textAlign: 'left',
    padding: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    whiteSpace: 'nowrap',
    fontWeight: tokens.fontWeightSemibold,
  },
  /** Header half of the pinned name column — same treatment as {@link name}. */
  thName: {
    position: 'sticky',
    left: 0,
    // Above the pinned data cells, which are themselves above the scrolling ones.
    zIndex: 2,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  thNum: { textAlign: 'right' },
  // Weekends are shaded rather than hidden: the source sheet highlights them, and a
  // driver working Saturday is exactly the case that produces overtime.
  weekend: { backgroundColor: tokens.colorNeutralBackground3 },
  /**
   * The driver's name STAYS PUT while the week scrolls.
   *
   * This grid is eleven columns — driver, seven days, then three totals — so on a
   * phone the name scrolls off the left and every row becomes an anonymous line
   * of numbers (the same failure the timecard had, Michael 2026-09-10). Cards are
   * the wrong answer here: seeing the whole week side by side is the point of
   * this table, so the week keeps scrolling and only the name is pinned.
   *
   * An explicit background is required — a sticky cell is transparent by default
   * and the scrolling columns would show through it.
   */
  name: {
    whiteSpace: 'nowrap',
    paddingRight: tokens.spacingHorizontalM,
    position: 'sticky',
    left: 0,
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  cell: {
    padding: '2px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ...shorthands.borderLeft('1px', 'solid', tokens.colorNeutralStroke2),
  },
  input: { width: '72px', fontVariantNumeric: 'tabular-nums' },
  num: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    padding: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    whiteSpace: 'nowrap',
  },
  overtime: {
    color: tokens.colorPaletteDarkOrangeForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  ptoCell: { textAlign: 'center', color: tokens.colorNeutralForeground3 },

  // ── status tints ────────────────────────────────────────────────────────────
  //
  // Colour is the FAST channel here — a reviewer scanning a week wants to see where
  // the unreviewed days are without reading anything. It is never the ONLY channel:
  // each cell also carries a tooltip naming its status, and the row's action button
  // states the count in words, so the grid is still usable to someone who cannot
  // distinguish the tints.
  pendingCell: { backgroundColor: tokens.colorPaletteYellowBackground1 },
  approvedCell: { backgroundColor: tokens.colorPaletteGreenBackground1 },
  postedCell: { backgroundColor: tokens.colorNeutralBackground3 },
  /** A posted figure is TEXT, not an input: 0042 freezes it and the API would 409. */
  postedValue: {
    display: 'block',
    textAlign: 'right',
    padding: tokens.spacingVerticalXS,
    fontVariantNumeric: 'tabular-nums',
    color: tokens.colorNeutralForeground2,
  },
  legend: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: tokens.spacingVerticalS,
  },
  swatch: {
    display: 'inline-block',
    width: '12px',
    height: '12px',
    marginRight: tokens.spacingHorizontalXS,
    verticalAlign: 'middle',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  actionCell: {
    padding: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    whiteSpace: 'nowrap',
  },
});

/** The tint for a cell's status. Posted is deliberately the most muted — it is done. */
function statusClass(status: DriverLogStatus, styles: ReturnType<typeof useStyles>): string {
  if (status === 'posted') {
    return styles.postedCell;
  }
  return status === 'approved' ? styles.approvedCell : styles.pendingCell;
}

/** What a cell's tooltip says. The status in words, and what it means for pay. */
function statusTooltip(status: DriverLogStatus, hours: number): string {
  if (status === 'posted') {
    return `Posted to NetSuite — ${hours} h are already on a TimeBill, so the hours and the date are frozen here.`;
  }
  if (status === 'approved') {
    return `Approved — ${hours} h will go on the next Post to NetSuite. Editing the hours withdraws the approval.`;
  }
  return `Pending review — ${hours} h will NOT post until someone approves them.`;
}

/** Weekday initial for a column header, from an ISO date (UTC, like the API). */
function dayLabel(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getUTCDay()] ?? '';
  return `${day} ${iso.slice(8)}`;
}

function isWeekend(iso: string): boolean {
  const day = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return day === 0 || day === 6;
}

/** Shift an ISO date by whole days, in UTC to match the API's week arithmetic. */
function shiftDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function DriverLogTable(): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();

  const [week, setWeek] = useState<DriverLogWeek | null>(null);
  const [weekOf, setWeekOf] = useState<string | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** Cell being edited, keyed `employeeId|day`, holding the raw input text. */
  const [draft, setDraft] = useState<{ key: string; value: string } | null>(null);

  /**
   * Entry ids by status, for the whole displayed week.
   *
   * Recomputed from the week the API returned rather than tracked alongside it: every
   * write replaces the week wholesale, so a separately-maintained count would be one
   * write behind exactly when it matters.
   */
  const ids = useMemo(() => {
    const pending: string[] = [];
    const approved: string[] = [];
    let posted = 0;
    for (const row of week?.rows ?? []) {
      for (const cell of row.cells) {
        if (cell.kind !== 'hours') {
          continue;
        }
        if (cell.status === 'posted') {
          posted += 1;
        } else if (cell.status === 'approved') {
          approved.push(cell.entryId);
        } else {
          pending.push(cell.entryId);
        }
      }
    }
    return { pending, approved, posted };
  }, [week]);

  const setApproval = useCallback(
    async (entryIds: readonly string[], approved: boolean): Promise<void> => {
      if (entryIds.length === 0) {
        return;
      }
      setSaving(true);
      setSaveError(null);
      try {
        setWeek(await api.setDriverLogApproval(entryIds, approved, week?.period.startDate));
      } catch (error) {
        setSaveError(
          error instanceof Error
            ? error.message
            : `Could not ${approved ? 'approve' : 'un-approve'} those hours.`
        );
      } finally {
        setSaving(false);
      }
    },
    [api, week]
  );

  useEffect(() => {
    let cancelled = false;
    setWeek(null);
    setLoadError(null);
    void (async () => {
      try {
        const data = await api.getDriverLogWeek(weekOf);
        if (!cancelled) {
          setWeek(data);
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : 'Could not load the week.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, weekOf]);

  const commit = useCallback(
    async (employeeId: string, day: string, raw: string, cell: DriverLogCell): Promise<void> => {
      setDraft(null);
      // A posted cell renders as text, so this is unreachable from the UI -- but the
      // guard stays, because the DB trigger would raise and the person would get a
      // 409 for something the grid should never have offered them.
      if (cell.kind === 'hours' && cell.status === 'posted') {
        return;
      }
      const trimmed = raw.trim();
      const existing = cell.kind === 'hours' ? cell.hours : null;

      // Nothing changed — don't spend a write (or a full week recompute) on it.
      if (trimmed === '' && existing === null) {
        return;
      }
      if (trimmed !== '' && existing !== null && Number(trimmed) === existing) {
        return;
      }

      setSaving(true);
      setSaveError(null);
      try {
        if (trimmed === '') {
          // Clearing is a DELETE, never a stored zero: a zero reads as "worked no
          // hours", which is a different claim from "nothing recorded".
          if (cell.kind === 'hours') {
            setWeek(await api.deleteDriverLog(cell.entryId));
          }
        } else {
          const hours = Number(trimmed);
          if (!Number.isFinite(hours)) {
            setSaveError(`"${trimmed}" is not a number.`);
            return;
          }
          setWeek(await api.upsertDriverLog({ employeeId, workDate: day, hours }));
        }
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : 'Could not save that cell.');
      } finally {
        setSaving(false);
      }
    },
    [api]
  );

  if (loadError !== null) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{loadError}</MessageBarBody>
      </MessageBar>
    );
  }
  if (week === null) {
    return <Spinner label="Loading the week…" />;
  }

  return (
    <div>
      <div className={styles.bar}>
        <Button
          onClick={() => {
            setWeekOf(shiftDays(week.period.startDate, -7));
          }}
          disabled={saving}
        >
          ‹ Previous week
        </Button>
        <Text weight="semibold">{week.period.label}</Text>
        <Button
          onClick={() => {
            setWeekOf(shiftDays(week.period.startDate, 7));
          }}
          disabled={saving}
        >
          Next week ›
        </Button>
        <Button
          onClick={() => {
            setWeekOf(undefined);
          }}
          disabled={saving}
        >
          This week
        </Button>
        <span className={styles.spacer} />
        {saving && <Spinner size="tiny" label="Saving…" />}
        <Button
          appearance="primary"
          disabled={saving || ids.pending.length === 0}
          onClick={() => {
            void setApproval(ids.pending, true);
          }}
        >
          Approve all pending ({ids.pending.length})
        </Button>
        <Text>
          {week.rows.length} {week.rows.length === 1 ? 'driver' : 'drivers'}
        </Text>
      </div>

      {/*
        WHAT ALREADY WENT, stated before the grid rather than left to be inferred from
        the tints. Driver hours posted with no review at all until 2026-09-16, so on
        any week from before that the honest thing to show a reviewer first is how much
        of it is already in NetSuite and beyond their reach.
      */}
      <div className={styles.legend}>
        <Text size={200}>
          <span className={mergeClasses(styles.swatch, styles.postedCell)} />
          {ids.posted} posted to NetSuite — frozen
        </Text>
        <Text size={200}>
          <span className={mergeClasses(styles.swatch, styles.approvedCell)} />
          {ids.approved.length} approved — goes on the next post
        </Text>
        <Text size={200}>
          <span className={mergeClasses(styles.swatch, styles.pendingCell)} />
          {ids.pending.length} pending review — will not post
        </Text>
      </div>

      {ids.pending.length > 0 && (
        <MessageBar intent="warning">
          <MessageBarBody>
            {ids.pending.length} {ids.pending.length === 1 ? 'day is' : 'days are'} waiting on
            review and will NOT post to NetSuite until approved. Approve a driver&rsquo;s row, or
            the whole week, then post from Hours &amp; Timesheet → Employee Timecards.
          </MessageBarBody>
        </MessageBar>
      )}

      {saveError !== null && (
        <MessageBar intent="error">
          <MessageBarBody>{saveError}</MessageBarBody>
        </MessageBar>
      )}

      {week.rows.length === 0 ? (
        <MessageBar intent="info">
          <MessageBarBody>
            No drivers found. The roster is everyone in the Driving department, so this is empty
            until someone is allocated to it.
          </MessageBarBody>
        </MessageBar>
      ) : (
        <div className={styles.scroller}>
          <table className={styles.grid}>
            <thead>
              <tr>
                <th className={mergeClasses(styles.th, styles.thName)}>Driver</th>
                {week.days.map((day) => (
                  <th
                    key={day}
                    className={mergeClasses(
                      styles.th,
                      styles.thNum,
                      isWeekend(day) ? styles.weekend : undefined
                    )}
                  >
                    {dayLabel(day)}
                  </th>
                ))}
                <th className={mergeClasses(styles.th, styles.thNum)}>Total</th>
                <th className={mergeClasses(styles.th, styles.thNum)}>Regular</th>
                <th className={mergeClasses(styles.th, styles.thNum)}>Overtime</th>
                <th className={styles.th}>Review</th>
              </tr>
            </thead>
            <tbody>
              {week.rows.map((row) => (
                <tr key={row.employeeId}>
                  <td className={mergeClasses(styles.num, styles.name)}>
                    {row.employeeName}
                    {row.overtimeExempt && (
                      // Otherwise "why is none of this overtime?" is a mystery.
                      <Tooltip
                        content="VISA employee — not eligible for overtime, so every hour stays regular."
                        relationship="description"
                      >
                        <Badge appearance="tint" color="informative" size="small">
                          No OT
                        </Badge>
                      </Tooltip>
                    )}
                  </td>

                  {week.days.map((day, index) => {
                    const cell = row.cells[index] ?? { kind: 'empty' as const };
                    const key = `${row.employeeId}|${day}`;
                    const weekendClass = isWeekend(day) ? styles.weekend : undefined;

                    if (cell.kind === 'pto') {
                      return (
                        <td
                          key={day}
                          className={mergeClasses(styles.cell, styles.ptoCell, weekendClass)}
                        >
                          <Tooltip
                            content={`On PTO — ${cell.hours} h from their booking. Edit it in the PTO report, not here.`}
                            relationship="description"
                          >
                            <Text size={200}>PTO</Text>
                          </Tooltip>
                        </td>
                      );
                    }

                    if (cell.kind === 'hours' && cell.status === 'posted') {
                      // Frozen by migration 0042 -- rendering an editable box here
                      // would offer a change the database will refuse.
                      return (
                        <td
                          key={day}
                          className={mergeClasses(styles.cell, styles.postedCell, weekendClass)}
                        >
                          <Tooltip
                            content={statusTooltip('posted', cell.hours)}
                            relationship="description"
                          >
                            <Text size={200} className={styles.postedValue}>
                              {cell.hours.toFixed(2)}
                            </Text>
                          </Tooltip>
                        </td>
                      );
                    }

                    const shown =
                      draft?.key === key
                        ? draft.value
                        : cell.kind === 'hours'
                          ? String(cell.hours)
                          : '';
                    return (
                      <td
                        key={day}
                        className={mergeClasses(
                          styles.cell,
                          weekendClass,
                          cell.kind === 'hours' ? statusClass(cell.status, styles) : undefined
                        )}
                        title={
                          cell.kind === 'hours' ? statusTooltip(cell.status, cell.hours) : undefined
                        }
                      >
                        <Input
                          className={styles.input}
                          size="small"
                          appearance="filled-lighter"
                          value={shown}
                          disabled={saving}
                          onChange={(_, data) => {
                            setDraft({ key, value: data.value });
                          }}
                          onBlur={() => {
                            if (draft?.key === key) {
                              void commit(row.employeeId, day, draft.value, cell);
                            }
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.currentTarget.blur();
                            } else if (event.key === 'Escape') {
                              setDraft(null);
                            }
                          }}
                        />
                      </td>
                    );
                  })}

                  <td className={styles.num}>{row.totalHours.toFixed(2)}</td>
                  <td className={styles.num}>{row.regularHours.toFixed(2)}</td>
                  <td
                    className={mergeClasses(
                      styles.num,
                      row.overtimeHours > 0 ? styles.overtime : undefined
                    )}
                  >
                    {row.overtimeHours.toFixed(2)}
                  </td>

                  <td className={styles.actionCell}>
                    <RowReview
                      cells={row.cells}
                      saving={saving}
                      onApprove={(entryIds) => {
                        void setApproval(entryIds, true);
                      }}
                      onUnapprove={(entryIds) => {
                        void setApproval(entryIds, false);
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * One driver's review control for the week.
 *
 * PER ROW, not per cell. A reviewer reads a person's week along the row and judges it
 * as a whole — "Randy's week looks right" — so a button per day would be six extra
 * clicks to say one thing. Anything genuinely doubtful is still separable: leave the
 * row unapproved, fix the cell, and approve after.
 *
 * The button shows the COUNT rather than just "Approve", because the row may be part
 * posted already and the number is the honest answer to "what am I about to clear?".
 */
function RowReview({
  cells,
  saving,
  onApprove,
  onUnapprove,
}: {
  cells: readonly DriverLogCell[];
  saving: boolean;
  onApprove: (entryIds: string[]) => void;
  onUnapprove: (entryIds: string[]) => void;
}): JSX.Element {
  const pending: string[] = [];
  const approved: string[] = [];
  let posted = 0;
  for (const cell of cells) {
    if (cell.kind !== 'hours') {
      continue;
    }
    if (cell.status === 'posted') {
      posted += 1;
    } else if (cell.status === 'approved') {
      approved.push(cell.entryId);
    } else {
      pending.push(cell.entryId);
    }
  }

  if (pending.length > 0) {
    return (
      <Button
        size="small"
        appearance="primary"
        disabled={saving}
        onClick={() => {
          onApprove(pending);
        }}
      >
        Approve {pending.length}
      </Button>
    );
  }

  if (approved.length > 0) {
    return (
      <>
        <Badge appearance="tint" color="success" size="small">
          Approved
        </Badge>{' '}
        <Button
          size="small"
          appearance="subtle"
          disabled={saving}
          onClick={() => {
            onUnapprove(approved);
          }}
        >
          Undo
        </Button>
      </>
    );
  }

  if (posted > 0) {
    return (
      <Tooltip
        content="Every logged day this week is already on a NetSuite TimeBill."
        relationship="description"
      >
        <Badge appearance="tint" color="informative" size="small">
          Posted
        </Badge>
      </Tooltip>
    );
  }

  return <Text size={200}>—</Text>;
}
